import type { Ticker } from "pixi.js";

/**
 * The single time source for presentation timing. Everything the
 * PresentationTaskRunner does (sleeps, tweens, continuous ticks) advances only
 * when a ticker listener is called with elapsed ENGINE seconds.
 *
 * - `PixiPresentationTicker` adapts the app ticker and adds pause / single-step
 *   (dev tools) — when paused, no listener is called, so every presentation
 *   timeline freezes in lockstep.
 * - `ManualPresentationTicker` is for tests and headless checks: the caller
 *   advances time explicitly and deterministically.
 */
export interface PresentationTicker {
  /** Registers a per-frame listener; returns the unsubscribe function. */
  add(listener: (deltaSeconds: number) => void): () => void;
  /**
   * Engine seconds dispatched so far — the presentation's "now". It stops while paused and moves
   * one frame per step, like every timeline the ticker drives; read it instead of the wall clock.
   */
  readonly elapsedSeconds: number;
}

export class ManualPresentationTicker implements PresentationTicker {
  private readonly listeners = new Set<(deltaSeconds: number) => void>();
  private elapsed = 0;

  public add(listener: (deltaSeconds: number) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Advances engine time by `seconds` in one frame. */
  public advance(seconds: number): void {
    this.elapsed += seconds;
    for (const listener of Array.from(this.listeners)) {
      listener(seconds);
    }
  }

  /** Advances engine time in fixed frames (default 60 fps) until `seconds` have elapsed. */
  public advanceInFrames(seconds: number, frameSeconds = 1 / 60): void {
    let remaining = seconds;
    while (remaining > 1e-9) {
      const step = Math.min(frameSeconds, remaining);
      this.advance(step);
      remaining -= step;
    }
  }

  public get listenerCount(): number {
    return this.listeners.size;
  }

  public get elapsedSeconds(): number {
    return this.elapsed;
  }
}

export class PixiPresentationTicker implements PresentationTicker {
  private readonly listeners = new Set<(deltaSeconds: number) => void>();
  private paused = false;
  private pendingStepSeconds = 0;
  private readonly pauseListeners = new Set<(paused: boolean) => void>();
  /** Caps a single frame's delta so a backgrounded tab does not jump a whole presentation. */
  private readonly maxDeltaSeconds: number;
  private elapsed = 0;

  public constructor(ticker: Ticker, options?: { maxDeltaSeconds?: number }) {
    this.maxDeltaSeconds = options?.maxDeltaSeconds ?? 0.1;
    ticker.add(this.onFrame, this);
  }

  public add(listener: (deltaSeconds: number) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    for (const listener of Array.from(this.pauseListeners)) listener(paused);
  }

  /** Notified on every pause / resume (the DOM chrome freezes its CSS animations with the game). */
  public subscribePaused(listener: (paused: boolean) => void): () => void {
    this.pauseListeners.add(listener);
    return () => {
      this.pauseListeners.delete(listener);
    };
  }

  public isPaused(): boolean {
    return this.paused;
  }

  public get elapsedSeconds(): number {
    return this.elapsed;
  }

  /** While paused: advances exactly one frame of `seconds` on the next tick. */
  public stepFrame(seconds = 1 / 60): void {
    this.pendingStepSeconds += seconds;
  }

  private onFrame(ticker: Ticker): void {
    let delta: number;
    if (this.paused) {
      if (this.pendingStepSeconds <= 0) return;
      delta = this.pendingStepSeconds;
      this.pendingStepSeconds = 0;
    } else {
      delta = Math.min(ticker.deltaMS / 1000, this.maxDeltaSeconds);
    }
    this.elapsed += delta;
    for (const listener of Array.from(this.listeners)) {
      listener(delta);
    }
  }
}
